import React, { useState, useMemo } from "react";
import { FiDatabase } from "react-icons/fi";
import { connectionIcons } from "utils/LogoMapper";

export const FeatureSection = ({ title, items, selectedItems, toggleItem }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const parsePrice = (price) => {
    if (!price || price.toLowerCase() === "free") return 0;
    const match = price.match(/\£?([\d]+)/);
    return match ? parseInt(match[1], 10) : Number.MAX_VALUE;
  };

  // --- dynamically generate filter options ---
  const statusOptions = useMemo(() => {
    const statuses = new Set(items.map((item) => item.status).filter(Boolean));
    return ["all", ...statuses];
  }, [items]);

  const categoryOptions = useMemo(() => {
    const categories = new Set(
      items.map((item) => item.category).filter(Boolean)
    );
    return ["all", ...categories];
  }, [items]);

  const filteredItems = useMemo(() => {
    const now = new Date();

    return items
      .filter((item) => {
        const matchesSearch =
          item.name.toLowerCase().includes(search.toLowerCase()) ||
          item.desc.toLowerCase().includes(search.toLowerCase());

        const matchesStatus =
          statusFilter === "all" || item.status === statusFilter;

        const matchesCategory =
          categoryFilter === "all" ||
          (item.category && item.category === categoryFilter);

        // Only include "coming" items if comingSoonDate is in the future
        const isComingValid =
          item.status !== "coming" ||
          !item.comingSoonDate ||
          new Date(item.comingSoonDate) >= now;

        return (
          matchesSearch && matchesStatus && matchesCategory && isComingValid
        );
      })
      .sort((a, b) => {
        // Included items first
        if (a.status === "included" && b.status !== "included") return -1;
        if (b.status === "included" && a.status !== "included") return 1;

        // Coming items sorted by comingSoonDate
        if (a.status === "coming" && b.status === "coming") {
          if (a.comingSoonDate && b.comingSoonDate) {
            return new Date(a.comingSoonDate) - new Date(b.comingSoonDate);
          }
          if (a.comingSoonDate) return -1;
          if (b.comingSoonDate) return 1;
        }

        // Otherwise sort by price
        return parsePrice(a.price) - parsePrice(b.price);
      });
  }, [items, search, statusFilter, categoryFilter]);

  return (
    <div className="w-full mt-16">
      {" "}
      {/* increased top margin */}
      <h2 className="text-lg font-semibold mb-6">{title}</h2>
      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-6">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 min-w-[200px]"
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {status === "all"
                ? "All Status"
                : status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {categoryOptions.map((cat) => (
            <option key={cat} value={cat}>
              {cat === "all"
                ? "All Categories"
                : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </option>
          ))}
        </select>
      </div>
      {/* Scrollable grid with custom scrollbar */}
      {/* Scrollable grid with improved layout */}
      <div className="max-h-[700px] overflow-y-auto pr-2 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => {
            const Icon = connectionIcons[item.name] || FiDatabase;

            return (
              <div
                key={item.name}
                className={`bg-white rounded-2xl shadow-md p-6 flex flex-col justify-between ${
                  item.status === "coming" ? "opacity-70" : ""
                }`}
              >
                <div className="flex flex-col items-center text-center mb-4">
                  <Icon className="h-12 w-12 mb-4 text-black" />
                  <h3 className="font-semibold">{item.name}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm font-medium">{item.price}</span>

                  {item.status === "coming" ? (
                    <span className="px-3 py-1 rounded-lg text-xs text-right bg-yellow-100 text-yellow-700 border border-yellow-300">
                      Coming Soon <br />
                      <span className="font-bold">
                        {item.comingSoonDate ? ` ${item.comingSoonDate}` : ""}
                      </span>
                    </span>
                  ) : item.status === "included" ? (
                    <span className="px-3 py-1 rounded-lg text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-300">
                      Included
                    </span>
                  ) : item.status === "purchased" ? (
                    <button
                      onClick={() => toggleItem(item)}
                      className="px-3 py-1 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 transition"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      onClick={() => toggleItem(item)}
                      className={`px-3 py-1 rounded-lg text-sm transition ${
                        selectedItems.includes(item.name)
                          ? "bg-green-600 text-white hover:bg-green-700"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      }`}
                    >
                      {selectedItems.includes(item) ? "Added" : "Add"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
